import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import ChurchNameInput from '../../components/ChurchNameInput';
import {
  listChurchNamesWithCounts,
  mergeChurchNames,
  type ChurchNameSuggestion,
} from '../../lib/api/churches';
import { useToast } from '../../contexts/ToastContext';
import { captureError } from '../../lib/capture-error';

const AdminChurchesPage: React.FC = () => {
  const { showToast } = useToast();
  const [churches, setChurches] = useState<ChurchNameSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNames, setSelectedNames] = useState<string[]>([]);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [canonicalName, setCanonicalName] = useState('');
  const [merging, setMerging] = useState(false);

  const loadChurches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listChurchNamesWithCounts();
      setChurches(data);
    } catch (err) {
      captureError(err, 'Failed to load church names');
      setError('Failed to load church names');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChurches();
  }, [loadChurches]);

  const filteredChurches = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return churches;
    return churches.filter((c) => c.name.toLowerCase().includes(q));
  }, [churches, searchQuery]);

  const toggleSelection = (name: string) => {
    setSelectedNames((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const openMergeModal = () => {
    if (selectedNames.length < 2) {
      showToast('Select at least two church names to merge', 'error');
      return;
    }
    const sorted = [...selectedNames].sort(
      (a, b) =>
        (churches.find((c) => c.name === b)?.usage_count || 0) -
        (churches.find((c) => c.name === a)?.usage_count || 0)
    );
    setCanonicalName(sorted[0] || '');
    setShowMergeModal(true);
  };

  const handleMerge = async () => {
    if (!canonicalName.trim()) {
      showToast('Enter a canonical church name', 'error');
      return;
    }
    setMerging(true);
    try {
      const count = await mergeChurchNames(selectedNames, canonicalName.trim());
      showToast(`Updated ${count} profile${count === 1 ? '' : 's'}`, 'success');
      setShowMergeModal(false);
      setSelectedNames([]);
      setCanonicalName('');
      await loadChurches();
    } catch (err) {
      captureError(err, 'Failed to merge church names');
      showToast(err instanceof Error ? err.message : 'Failed to merge church names', 'error');
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="container my-5">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <Link to="/admin" className="text-muted small text-decoration-none">
            <i className="fas fa-arrow-left me-1" aria-hidden></i>
            Admin Dashboard
          </Link>
          <h1 className="mb-0 mt-1">Church Names</h1>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          disabled={selectedNames.length < 2}
          onClick={openMergeModal}
        >
          <i className="fas fa-compress-arrows-alt me-2" aria-hidden></i>
          Merge Selected ({selectedNames.length})
        </button>
      </div>

      <p className="text-muted mb-4">
        Normalize spelling variants by merging them into one canonical church name. Applicant reports group by these names.
      </p>

      <div className="mb-3">
        <input
          type="search"
          className="form-control"
          placeholder="Search church names..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th style={{ width: '48px' }}></th>
                <th>Church Name</th>
                <th className="text-end">Applicants</th>
              </tr>
            </thead>
            <tbody>
              {filteredChurches.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-center text-muted py-4">
                    No church names found.
                  </td>
                </tr>
              ) : (
                filteredChurches.map((church) => (
                  <tr key={church.name}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedNames.includes(church.name)}
                        onChange={() => toggleSelection(church.name)}
                        aria-label={`Select ${church.name}`}
                      />
                    </td>
                    <td>{church.name}</td>
                    <td className="text-end">
                      <span className="badge bg-secondary">{church.usage_count}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Modal show={showMergeModal} onHide={() => !merging && setShowMergeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Merge Church Names</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            These variants will be combined into one canonical name across all attendee profiles:
          </p>
          <ul>
            {selectedNames.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </ul>
          <label className="form-label">Canonical name</label>
          <ChurchNameInput
            value={canonicalName}
            onChange={setCanonicalName}
            className="form-control"
            placeholder="Enter the preferred church name"
          />
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowMergeModal(false)}
            disabled={merging}
          >
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={handleMerge} disabled={merging}>
            {merging ? 'Merging...' : 'Merge Names'}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminChurchesPage;
